import { useQuery } from '@tanstack/react-query';
import { fetchReference } from '../../lib/lesson-api.js';
import { localName, t } from '../../lib/i18n.js';

/**
 * Which business units and projects a grant or a delegation reaches (FN-1).
 * Nothing ticked means the whole application — said in words, because an
 * empty list reads as "nothing" to anyone who has not been told otherwise.
 */

export interface ScopeValue {
  businessUnitIds: string[];
  projectIds: string[];
}

export function useScopeLists(app: string) {
  const reference = useQuery({
    queryKey: ['reference', app],
    queryFn: () => fetchReference(app),
    staleTime: 10 * 60_000,
  });
  return {
    units: reference.data?.businessUnits ?? [],
    projects: reference.data?.projects ?? [],
  };
}

/** "CONST; ALPHA, BETA" — or "the whole application". */
export function describeScope(app: string, scope: ScopeValue, lists: ReturnType<typeof useScopeLists>): string {
  if (scope.businessUnitIds.length === 0 && scope.projectIds.length === 0) return 'the whole application';
  const units = scope.businessUnitIds.map((id) => lists.units.find((unit) => unit.id === id)?.code ?? 'a business unit');
  const projects = scope.projectIds.map((id) => lists.projects.find((project) => project.id === id)?.code ?? 'a project');
  return [...units, ...projects].join(', ') || `part of ${app}`;
}

export function ScopeFields({
  app,
  value,
  onChange,
  legend,
}: {
  app: string;
  value: ScopeValue;
  onChange: (next: ScopeValue) => void;
  legend: string;
}) {
  const { units, projects } = useScopeLists(app);
  const toggle = (list: 'businessUnitIds' | 'projectIds', id: string) => {
    const current = value[list];
    onChange({ ...value, [list]: current.includes(id) ? current.filter((each) => each !== id) : [...current, id] });
  };

  return (
    <fieldset className="m-0 rounded border border-border p-2 text-xs">
      <legend className="px-1 text-content-secondary">{legend}</legend>
      <p className="m-0 text-content-secondary">
        {value.businessUnitIds.length + value.projectIds.length === 0
          ? t("Nothing ticked: the whole application.")
          : t("Only what is ticked. A business unit includes all its projects.")}
      </p>
      <div className="mt-1 grid gap-2 sm:grid-cols-2">
        <div>
          <p className="m-0 font-medium">{t("Business units")}</p>
          {units.map((unit) => (
            <label key={unit.id} className="flex items-center gap-1">
              <input
                type="checkbox"
                checked={value.businessUnitIds.includes(unit.id)}
                onChange={() => toggle('businessUnitIds', unit.id)}
              />
              {unit.code} — {localName(unit)}
            </label>
          ))}
        </div>
        <div>
          <p className="m-0 font-medium">{t("Projects")}</p>
          {projects.map((project) => (
            <label key={project.id} className="flex items-center gap-1">
              <input
                type="checkbox"
                checked={value.projectIds.includes(project.id)}
                onChange={() => toggle('projectIds', project.id)}
              />
              {project.code} — {localName(project)}
            </label>
          ))}
        </div>
      </div>
    </fieldset>
  );
}
